import type { Driver, Hire, Organization, Ticket } from '../types';

export type DriverTicketNoticeType = 'pco_hire_notice' | 'fleet_evidence_request';

export interface DriverTicketRecipient {
  email: string;
  name: string;
  source: 'linked_driver' | 'linked_hire' | 'ticket_assignment';
}

export interface DriverTicketNoticePayload {
  ticketId: string;
  organisationId: string;
  noticeType: DriverTicketNoticeType;
  driverName: string;
  pcnNumber: string;
  vrn: string;
  issuingAuthority: string;
  location: string;
  amount: number;
  contraventionDate?: string;
  issueDate?: string;
  operatorName: string;
  operatorNote?: string;
}

const clean = (value?: string | null): string => String(value || '').trim();

export const resolveDriverTicketRecipient = (
  ticket: Ticket,
  linkedHire?: Hire | null,
  linkedDriver?: Driver | null
): DriverTicketRecipient | null => {
  const driverEmail = clean(linkedDriver?.email);
  if (driverEmail) {
    return {
      email: driverEmail,
      name: clean(linkedDriver?.name) || clean(ticket.assigned_driver_name) || clean(linkedHire?.driver_name) || 'Driver',
      source: 'linked_driver'
    };
  }

  const hireEmail = clean(linkedHire?.driver_email);
  if (hireEmail) {
    return {
      email: hireEmail,
      name: clean(linkedHire?.driver_name) || clean(ticket.assigned_driver_name) || 'Driver',
      source: 'linked_hire'
    };
  }

  const assignedEmail = clean(ticket.assigned_driver_email);
  if (assignedEmail) {
    return {
      email: assignedEmail,
      name: clean(ticket.assigned_driver_name) || 'Driver',
      source: 'ticket_assignment'
    };
  }

  return null;
};

export const getDriverTicketNoticeType = (
  organization?: Pick<Organization, 'business_model'> | null
): DriverTicketNoticeType =>
  organization?.business_model === 'fleet_operator' ? 'fleet_evidence_request' : 'pco_hire_notice';

export const getDriverTicketNoticeSubject = (
  ticket: Pick<Ticket, 'pcn_number' | 'vrn'>,
  noticeType: DriverTicketNoticeType
): string => {
  const pcn = clean(ticket.pcn_number) || clean(ticket.vrn) || 'parking notice';
  if (noticeType === 'fleet_evidence_request') {
    return `Action needed: confirm details for PCN ${pcn}`;
  }
  return `PCN received for your hire vehicle: ${pcn}`;
};

export const getDefaultDriverTicketNoticeNote = (noticeType: DriverTicketNoticeType): string => {
  if (noticeType === 'fleet_evidence_request') {
    return 'Please reply with any context, photos, delivery notes, receipts, or other information that may help us review this PCN.';
  }

  return 'This notice is for your information while we review liability and recovery under the hire agreement. No driver invoice is created by this notification.';
};

export const buildDriverTicketNoticePayload = (
  ticket: Ticket,
  organization: Pick<Organization, 'id' | 'name' | 'business_model'> | null,
  recipient: DriverTicketRecipient,
  operatorNote?: string
): DriverTicketNoticePayload => {
  const noticeType = getDriverTicketNoticeType(organization);
  return {
    ticketId: ticket.id,
    organisationId: ticket.organisation_id,
    noticeType,
    driverName: recipient.name,
    pcnNumber: clean(ticket.pcn_number),
    vrn: clean(ticket.vrn),
    issuingAuthority: clean(ticket.issuing_authority),
    location: clean(ticket.location),
    amount: Number(ticket.amount || 0),
    contraventionDate: ticket.contravention_date,
    issueDate: ticket.issue_date,
    operatorName: clean(organization?.name) || 'PCNGuard Fleet',
    operatorNote: clean(operatorNote)
  };
};

export const DriverNotificationService = {
  buildDriverTicketNoticePayload,
  getDefaultDriverTicketNoticeNote,
  getDriverTicketNoticeSubject,
  getDriverTicketNoticeType,
  resolveDriverTicketRecipient
};
